import { ITypeFieldFilterOptions } from '~/client/graph/types.generated'

import { getAllowedFilterValues } from './getAllowedFilterValues'

export const workflowFilterPropMap = {
  TYPE: 'initialType',
} as const

export function getAllowedWorkflowTypes(
  filterOptions: ITypeFieldFilterOptions[] | null | undefined,
): string[] {
  return getAllowedFilterValues(filterOptions, workflowFilterPropMap.TYPE)
}

/**
 * The configured type restriction resolved against the types that still exist.
 *
 * A restriction names types by their `type` string, which outlives the type
 * itself: deleting a workflow type leaves the string behind in every field that
 * referenced it. Resolving through the store drops those, so a restriction whose
 * types are ALL deleted collapses to an empty list — and an empty list is what
 * every consumer already reads as "no restriction".
 *
 * Consumers must share this rather than compare the raw strings, or they drift:
 * one side resolves and offers everything, the other matches literally and
 * offers nothing, for the same field.
 *
 * Generic in the resolved type so this module keeps its store-free imports.
 */
export function resolveAllowedWorkflowTypes<T>(
  filterOptions: ITypeFieldFilterOptions[] | null | undefined,
  resolveType: (type: string) => T | null | undefined,
): T[] {
  return getAllowedWorkflowTypes(filterOptions)
    .map(resolveType)
    .filter(Boolean) as T[]
}

/**
 * Whether a workflow may be offered for a field, given that field's resolved
 * restriction.
 *
 * Two rules, both of which have regressed before:
 *   - a workflow whose own type no longer exists is never selectable, even for
 *     an unrestricted field. Pass the type resolved via
 *     `getActualTypeByAnyVersionId`, which returns nothing for a deleted type
 *     while still mapping a superseded version id to its live type.
 *   - an empty `allowedTypes` means unrestricted, not "nothing allowed".
 */
export function isWorkflowTypeSelectable(
  resolvedType: { type?: string | null } | null | undefined,
  allowedTypes: string[],
): boolean {
  if (!resolvedType?.type) return false
  return !allowedTypes.length || allowedTypes.includes(resolvedType.type)
}
